import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
    PENDING,
    VALIDATED,
    CANCELED,
    INSUFFICIENT_FOUND,
    WAITING_TRANSACTION
} from '../../../../services/checkout/constants';
import { PATH } from '../../../../constants/path';
import { KYC_USE_CASE_WAITING_TRANSACTION } from '../../../../services/kyc/constants';
import { MODAL_TYPE_KYC, MODAL_TYPE_LOGIN } from '../../../../services/modal/constants';

import { showModal } from '../../../../services/modal/actions';
import { fetchPaypalDetails } from '../../../../services/paypal/actions';
import { fetchCheckoutDetails } from '../../../../services/checkout/actions';
import { fetchEnterpriseCardDetails } from '../../../../services/enterprisecard/actions';
import { fetchEbonOrderByIdWithCode } from '../../../../services/ebon/actions';
import { fetchUserInfo } from '../../../../services/account/myprofile/actions';
import { logoutUser } from '../../../../services/authentication/logout/actions';
import { fetchUserBalance } from '../../../../services/account/balance/actions';

import { useTracking } from '../../../../tracking/provider';
import { isTokenExpired } from '../../../../services/authentication/utils';

const ConfirmationEbonPageStateController = ({
    orderId,
    partnerId,
    partnerName,
    checkoutSessionId,
    checkoutPaymentId,
    paypalCost,
    paymentRef,
    isEnterpriseCardPayment,
    setLoading,
    setKyc,
    kycValidated,
    ebonOrderState,
    setEbonOrderState,
    detailsInterval,
    sessionEmail,
    history,
    fetchingOrder,
    checkoutDetails,
    confirmedOrderState,
    paypalDetails,
    enterpriseCardDetails,
    token,
    isAuthenticated,
    fetchEbonOrderByIdWithCode,
    fetchCheckoutDetails,
    fetchPaypalDetails,
    fetchEnterpriseCardDetails,
    showModal,
    logoutUser,
    fetchUserBalance
}) => {
    // ref : l'id doit rester lisible par le nettoyage au démontage
    const pendingInterval = useRef(null);

    const { gtm, ready } = useTracking();

    const isPaypalPayment = !!paypalCost;
    // Checkout par défaut : Apple Pay / Google Pay reviennent parfois sans cko-*
    const isCheckoutPayment = !isPaypalPayment && !isEnterpriseCardPayment && !!orderId;

    const fetchDetails = () => {
        if (isEnterpriseCardPayment) {
            fetchEnterpriseCardDetails(orderId);
        } else if (isCheckoutPayment) {
            fetchCheckoutDetails(orderId, checkoutPaymentId);
        } else if (isPaypalPayment) {
            fetchPaypalDetails(paymentRef);
        }
    };

    /* Authentication check */
    useEffect(() => {
        checkAuthentication();
    });

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    const checkAuthentication = () => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(PATH.HOME_PATH);
                resolve();
            }).then(() => logoutUser(() => showModal(MODAL_TYPE_LOGIN)));
        }
    };

    /* Handle loading */
    useEffect(() => {
        // sans état PSP, l'état de la commande suffit
        const orderAlreadyValidated = confirmedOrderState === VALIDATED;

        const waitingForEbonState =
            ((isCheckoutPayment || isPaypalPayment || isEnterpriseCardPayment) &&
                !ebonOrderState &&
                !orderAlreadyValidated) ||
            ebonOrderState === PENDING;

        // pas de loader pendant les appels details : le polling ne doit pas masquer une commande validée
        setLoading(Boolean(fetchingOrder || waitingForEbonState));
    }, [fetchingOrder, ebonOrderState, confirmedOrderState]);

    /* Fetch order on display */
    useEffect(() => {
        if (isMemberAuthenticated()) {
            fetchEbonOrderByIdWithCode(orderId);
            fetchUserInfo();
        }
    }, []);

    /* Fetch order state on display */
    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchDetails();
        }, 1000);
        return () => clearTimeout(timeout);
    }, [checkoutSessionId, paypalCost]);

    /* Stop polling when leaving the page */
    useEffect(() => {
        return () => clearInterval(pendingInterval.current);
    }, []);

    // Delay balance fetching because balance update is not instantaneous on server side
    useEffect(() => {
        setTimeout(() => {
            fetchUserBalance();
        }, 1000);
    }, []);

    /* Set order state */
    useEffect(() => {
        if (isPaypalPayment) {
            return setEbonOrderState(paypalDetails);
        } else if (isEnterpriseCardPayment) {
            return setEbonOrderState(enterpriseCardDetails);
        } else {
            return setEbonOrderState(checkoutDetails);
        }
    }, [checkoutDetails, paypalDetails, enterpriseCardDetails]);

    /* Handle order states */
    useEffect(() => {
        if ((!ebonOrderState || ebonOrderState === PENDING) && !pendingInterval.current) {
            pendingInterval.current = setInterval(() => {
                fetchDetails();
            }, 5000);
        }

        if (!kycValidated && ebonOrderState && ebonOrderState === WAITING_TRANSACTION) {
            clearInterval(pendingInterval.current);
            clearInterval(detailsInterval);
            setKyc(true);
            showModal(MODAL_TYPE_KYC, {
                hideCloseButton: true,
                useCase: KYC_USE_CASE_WAITING_TRANSACTION
            });
        }

        if (ebonOrderState === CANCELED || ebonOrderState === INSUFFICIENT_FOUND) {
            clearInterval(pendingInterval.current);
            clearInterval(detailsInterval);
            history.push(
                isEnterpriseCardPayment
                    ? `${PATH.EBON_ERROR_PATH}-${partnerId}?paiement=error&provider=unavailable`
                    : `${PATH.EBON_ERROR_PATH}-${partnerId}?paiement=error&orderId=${orderId}`
            );
        }

        if (ebonOrderState === VALIDATED) {
            ready &&
                gtm.event('purchase', {
                    partner: { name: partnerName, id: partnerId },
                    orderId,
                    email: sessionEmail
                });
            clearInterval(pendingInterval.current);
            clearInterval(detailsInterval);

            // bon livré en temps réel : codes et solde e-card à jour
            if (isEnterpriseCardPayment && confirmedOrderState !== VALIDATED) {
                fetchEbonOrderByIdWithCode(orderId);
                fetchUserBalance();
            }
        }
    }, [ebonOrderState]);

    return null;
};

ConfirmationEbonPageStateController.propTypes = {
    orderId: PropTypes.string,
    partnerName: PropTypes.string,
    partnerId: PropTypes.number,
    checkoutSessionId: PropTypes.string,
    checkoutPaymentId: PropTypes.string,
    paypalCost: PropTypes.string,
    paymentRef: PropTypes.string,
    isEnterpriseCardPayment: PropTypes.bool,
    setLoading: PropTypes.func,
    setKyc: PropTypes.func,
    kycValidated: PropTypes.bool,
    ebonOrderState: PropTypes.string,
    confirmedOrderState: PropTypes.string,
    setEbonOrderState: PropTypes.func,
    detailsInterval: PropTypes.number,
    sessionEmail: PropTypes.string
};

const mapStateToProps = state => {
    return {
        fetchingOrder: state.ebon.fetchOrder,
        checkoutDetails: state.checkout.details ? state.checkout.details.ebonOrderState : null,
        confirmedOrderState: state.ebon.confirmedOrder ? state.ebon.confirmedOrder.state : null,
        paypalDetails: state.paypal.details ? state.paypal.details.ebonOrderState : null,
        enterpriseCardDetails: state.enterprisecard.details
            ? state.enterprisecard.details.ebonOrderState
            : null,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated
    };
};

const mapDispatchToProps = {
    fetchEbonOrderByIdWithCode,
    fetchCheckoutDetails,
    fetchPaypalDetails,
    fetchEnterpriseCardDetails,
    showModal,
    logoutUser,
    fetchUserBalance
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ConfirmationEbonPageStateController)
);



// WEBPACK FOOTER //
// ./src/components/pages/ebon/confirmation/ConfirmationEbonPageStateController.js